import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
    QuestionMarkCircleIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    HomeIcon,
    UsersIcon,
    CurrencyDollarIcon,
    ClipboardDocumentListIcon,
    BookOpenIcon,
    AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { QuestionMarkCircleIcon as QuestionMarkCircleSolidIcon } from '@heroicons/react/24/solid';

// Tour step interface with route navigation
export interface TourStep {
    id: string;
    route: string; // Route to navigate to for this step
    target?: string; // CSS selector for element to highlight
    title: string;
    description: string;
    icon?: React.ReactNode;
    waitForElement?: boolean; // Wait for target element to appear
}

// Storage keys
const STORAGE_KEY = 'tourGuide_dismissed';
const FIRST_VISIT_KEY = 'tourGuide_firstVisit';

// User tour steps (regular dashboard)
const userTourSteps: TourStep[] = [
    {
        id: 'welcome',
        route: '/dashboard',
        title: 'Welcome to Sales Toast! 🎉',
        description: 'Let\'s take you on a quick tour of all the powerful features. We\'ll navigate through different pages and show you what you can do.',
        icon: <HomeIcon className="w-6 h-6" />,
    },
    {
        id: 'dashboard',
        route: '/dashboard',
        target: '[data-tour="dashboard-content"]',
        title: 'Your Dashboard',
        description: 'This is your command center. View key metrics, recent activities, and get an overview of your sales performance at a glance.',
        icon: <HomeIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'clients',
        route: '/clients',
        target: '[data-tour="page-content"]',
        title: 'Client Management',
        description: 'Manage all your clients here. Add new clients, view their details, track interactions, and organize them with segments.',
        icon: <UsersIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'deals',
        route: '/deals',
        target: '[data-tour="page-content"]',
        title: 'Deals & Pipeline',
        description: 'Track your deals through every stage. Use the pipeline board view to drag and drop deals, monitor values, and close more sales.',
        icon: <CurrencyDollarIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'tasks',
        route: '/tasks',
        target: '[data-tour="page-content"]',
        title: 'Task Management',
        description: 'Stay organized with your task command center. Create, prioritize, and track all your follow-ups and to-dos in one place.',
        icon: <ClipboardDocumentListIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'catalogue',
        route: '/catalogue',
        target: '[data-tour="page-content"]',
        title: 'Product Catalogue',
        description: 'Browse your complete product catalogue. View items, check availability, and access product information quickly.',
        icon: <BookOpenIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'training',
        route: '/training',
        target: '[data-tour="page-content"]',
        title: 'Training Center',
        description: 'Access training videos and materials. Complete required training modules and track your learning progress.',
        icon: <AcademicCapIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'complete',
        route: '/dashboard',
        title: 'You\'re All Set! 🚀',
        description: 'You\'ve completed the tour! Click the help button anytime to restart. Now go ahead and start closing deals!',
        icon: <CheckCircleIcon className="w-6 h-6" />,
    },
];

// Admin tour steps
const adminTourSteps: TourStep[] = [
    {
        id: 'welcome',
        route: '/admin',
        title: 'Welcome, Admin! 👋',
        description: 'Let\'s explore the admin panel. You have access to powerful management tools to oversee your entire organization.',
        icon: <HomeIcon className="w-6 h-6" />,
    },
    {
        id: 'admin-dashboard',
        route: '/admin',
        target: '[data-tour="page-content"]',
        title: 'Admin Dashboard',
        description: 'Your central hub for monitoring business performance. View metrics, check team activity, and access quick insights.',
        icon: <HomeIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'users',
        route: '/admin/users',
        target: '[data-tour="page-content"]',
        title: 'User Management',
        description: 'Manage your team members. Add new users, assign roles, set permissions, and monitor user activity across the platform.',
        icon: <UsersIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'clients-admin',
        route: '/admin/clients',
        target: '[data-tour="page-content"]',
        title: 'All Clients',
        description: 'View and manage all clients across your organization. Assign clients to salespeople and monitor relationships.',
        icon: <UsersIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'deals-admin',
        route: '/admin/deals',
        target: '[data-tour="page-content"]',
        title: 'All Orders',
        description: 'Access every deal in the system. Track order status, monitor sales performance, and manage the entire sales pipeline.',
        icon: <CurrencyDollarIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'inventory',
        route: '/admin/inventory',
        target: '[data-tour="page-content"]',
        title: 'Inventory Management',
        description: 'Control your product inventory. Track stock levels, manage products, set pricing, and monitor inventory health.',
        icon: <BookOpenIcon className="w-6 h-6" />,
        waitForElement: true,
    },
    {
        id: 'complete',
        route: '/admin',
        title: 'Tour Complete! 🎉',
        description: 'You now know your way around the admin panel. Use the help button anytime to revisit this tour.',
        icon: <CheckCircleIcon className="w-6 h-6" />,
    },
];

interface SpotlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface TourGuideProps {
    isAdmin?: boolean;
}

export const TourGuide: React.FC<TourGuideProps> = ({ isAdmin = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [showPulse, setShowPulse] = useState(false);
    const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const observerRef = useRef<MutationObserver | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const tourSteps = isAdmin ? adminTourSteps : userTourSteps;
    const currentTourStep = tourSteps[currentStep];

    // Check if first visit
    useEffect(() => {
        const hasVisited = localStorage.getItem(FIRST_VISIT_KEY);
        if (!hasVisited) {
            setShowPulse(true);
        }
    }, []);

    // Find and highlight target element
    const findAndHighlightElement = useCallback(() => {
        if (!currentTourStep?.target) {
            setSpotlightRect(null);
            return;
        }

        const element = document.querySelector(currentTourStep.target) as HTMLElement;
        if (element) {
            const rect = element.getBoundingClientRect();
            setSpotlightRect({
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                height: rect.height,
            });

            // Scroll element into view smoothly
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setIsTransitioning(false);
        } else if (currentTourStep.waitForElement) {
            // Set up mutation observer to wait for element
            if (observerRef.current) {
                observerRef.current.disconnect();
            }

            observerRef.current = new MutationObserver(() => {
                const el = document.querySelector(currentTourStep.target!) as HTMLElement;
                if (el) {
                    const rect = el.getBoundingClientRect();
                    setSpotlightRect({
                        top: rect.top + window.scrollY,
                        left: rect.left + window.scrollX,
                        width: rect.width,
                        height: rect.height,
                    });
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setIsTransitioning(false);
                    observerRef.current?.disconnect();
                }
            });

            observerRef.current.observe(document.body, {
                childList: true,
                subtree: true,
            });

            // Fallback timeout
            timeoutRef.current = setTimeout(() => {
                setSpotlightRect(null);
                setIsTransitioning(false);
            }, 2000);
        } else {
            setSpotlightRect(null);
            setIsTransitioning(false);
        }
    }, [currentTourStep]);

    // Navigate and highlight when step changes
    useEffect(() => {
        if (!isOpen) return;

        const currentPath = location.pathname;
        const targetPath = currentTourStep?.route;

        if (targetPath && currentPath !== targetPath) {
            setIsTransitioning(true);
            navigate(targetPath);

            // Wait for navigation and then find element
            setTimeout(findAndHighlightElement, 500);
        } else {
            setTimeout(findAndHighlightElement, 100);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isOpen, currentStep, currentTourStep, location.pathname, navigate, findAndHighlightElement]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'Escape':
                    handleClose();
                    break;
                case 'ArrowRight':
                case 'ArrowDown':
                    handleNext();
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    handlePrev();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentStep]);

    // Recalculate spotlight on window resize
    useEffect(() => {
        if (!isOpen) return;

        const handleResize = () => findAndHighlightElement();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen, findAndHighlightElement]);

    const handleOpen = useCallback(() => {
        setIsOpen(true);
        setCurrentStep(0);
        setShowPulse(false);
        localStorage.setItem(FIRST_VISIT_KEY, 'true');
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setSpotlightRect(null);
        setCurrentStep(0);
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
    }, []);

    const handleNext = useCallback(() => {
        if (currentStep < tourSteps.length - 1) {
            setIsTransitioning(true);
            setCurrentStep(prev => prev + 1);
        } else {
            handleClose();
        }
    }, [currentStep, tourSteps.length, handleClose]);

    const handlePrev = useCallback(() => {
        if (currentStep > 0) {
            setIsTransitioning(true);
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    const handleSkip = useCallback(() => {
        localStorage.setItem(STORAGE_KEY, 'true');
        handleClose();
    }, [handleClose]);

    const handleStepClick = useCallback((index: number) => {
        if (index !== currentStep) {
            setIsTransitioning(true);
            setCurrentStep(index);
        }
    }, [currentStep]);

    const isLastStep = currentStep === tourSteps.length - 1;
    const isFirstStep = currentStep === 0;
    const progress = ((currentStep + 1) / tourSteps.length) * 100;

    // Calculate tooltip position based on spotlight - smart positioning
    const getTooltipStyle = (): React.CSSProperties => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const tooltipWidth = Math.min(400, viewportWidth - 40);
        const tooltipHeight = 300;
        const padding = 24;

        // No spotlight - center the tooltip
        if (!spotlightRect) {
            return {
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: `${tooltipWidth}px`,
            };
        }

        const spotlightCenterX = spotlightRect.left + spotlightRect.width / 2;
        const spotlightCenterY = (spotlightRect.top - window.scrollY) + spotlightRect.height / 2;

        // Determine if spotlight is on left or right half of screen
        const isSpotlightOnRight = spotlightCenterX > viewportWidth / 2;
        const isSpotlightOnBottom = spotlightCenterY > viewportHeight / 2;

        let top: number;
        let left: number;

        // Horizontal positioning: put tooltip on opposite side of spotlight
        if (isSpotlightOnRight) {
            // Spotlight on right, put tooltip on left
            left = padding;
        } else {
            // Spotlight on left, put tooltip on right
            left = viewportWidth - tooltipWidth - padding;
        }

        // Vertical positioning: try to align with spotlight center, but keep in bounds
        if (isSpotlightOnBottom) {
            // Spotlight is in bottom half, position tooltip higher
            top = Math.max(padding, Math.min(spotlightCenterY - tooltipHeight / 2, viewportHeight - tooltipHeight - padding - 60));
        } else {
            // Spotlight is in top half, position tooltip lower
            top = Math.max(padding + 60, Math.min(spotlightCenterY - tooltipHeight / 2, viewportHeight - tooltipHeight - padding));
        }

        // Ensure tooltip doesn't go off screen vertically
        top = Math.max(padding, Math.min(top, viewportHeight - tooltipHeight - padding - 60));

        return {
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            width: `${tooltipWidth}px`,
        };
    };

    return (
        <>
            {/* Fixed Help Button */}
            <button
                onClick={handleOpen}
                className={clsx(
                    'fixed z-[60] flex items-center justify-center',
                    'w-14 h-14 md:w-16 md:h-16',
                    'rounded-full shadow-2xl',
                    'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500',
                    'hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600',
                    'text-white',
                    'transition-all duration-300 ease-out',
                    'hover:scale-110 hover:shadow-purple-500/40',
                    'focus:outline-none focus:ring-4 focus:ring-purple-400/50',
                    'bottom-6 right-6 md:bottom-8 md:right-8',
                    'group'
                )}
                aria-label="Start tour guide"
                title="Help & Tour Guide"
            >
                <QuestionMarkCircleSolidIcon className="w-7 h-7 md:w-8 md:h-8 group-hover:scale-110 transition-transform" />

                {/* Pulse rings for first visit */}
                {showPulse && (
                    <>
                        <span className="absolute inset-0 rounded-full bg-purple-400 animate-ping opacity-40" />
                        <span className="absolute inset-0 rounded-full bg-purple-300 animate-ping opacity-20" style={{ animationDelay: '0.5s' }} />
                    </>
                )}
            </button>

            {/* Tour Overlay */}
            {isOpen && (
                <div className="fixed inset-0 z-[1000] overflow-hidden">
                    {/* SVG Mask for Spotlight Effect */}
                    <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
                        <defs>
                            <mask id="spotlight-mask">
                                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                {spotlightRect && (
                                    <rect
                                        x={spotlightRect.left - 12}
                                        y={spotlightRect.top - window.scrollY - 12}
                                        width={spotlightRect.width + 24}
                                        height={spotlightRect.height + 24}
                                        rx="16"
                                        fill="black"
                                        className="transition-all duration-500 ease-out"
                                    />
                                )}
                            </mask>
                        </defs>
                        <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            fill="rgba(15, 23, 42, 0.85)"
                            mask="url(#spotlight-mask)"
                            className="transition-all duration-300"
                        />
                    </svg>

                    {/* Spotlight border glow */}
                    {spotlightRect && (
                        <div
                            className="absolute pointer-events-none transition-all duration-500 ease-out"
                            style={{
                                top: spotlightRect.top - window.scrollY - 12,
                                left: spotlightRect.left - 12,
                                width: spotlightRect.width + 24,
                                height: spotlightRect.height + 24,
                                borderRadius: '16px',
                                boxShadow: '0 0 0 4px rgba(139, 92, 246, 0.6), 0 0 30px rgba(139, 92, 246, 0.4)',
                            }}
                        />
                    )}

                    {/* Close button (top-right) */}
                    <button
                        onClick={handleClose}
                        className="fixed top-6 right-6 z-[1001] p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-200"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>

                    {/* Tour Step Card */}
                    <div
                        className={clsx(
                            'z-[1001] bg-white rounded-2xl shadow-2xl overflow-hidden',
                            'border border-gray-100',
                            'transition-all duration-500 ease-out',
                            isTransitioning && 'opacity-80 scale-98'
                        )}
                        style={getTooltipStyle()}
                    >
                        {/* Card Header with Gradient */}
                        <div className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-6 py-5">
                            {/* Progress bar background */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
                                <div
                                    className="h-full bg-white transition-all duration-500 ease-out"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                {/* Icon */}
                                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-white">
                                    {currentTourStep?.icon || <QuestionMarkCircleIcon className="w-6 h-6" />}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="px-2 py-0.5 text-xs font-semibold bg-white/20 rounded-full text-white">
                                            {currentStep + 1} / {tourSteps.length}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-bold text-white truncate">
                                        {currentTourStep?.title}
                                    </h3>
                                </div>
                            </div>
                        </div>

                        {/* Card Body */}
                        <div className="px-6 py-5">
                            <p className="text-gray-600 leading-relaxed text-[15px]">
                                {currentTourStep?.description}
                            </p>
                        </div>

                        {/* Step dots indicator */}
                        <div className="px-6 pb-4">
                            <div className="flex items-center justify-center gap-1.5">
                                {tourSteps.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleStepClick(index)}
                                        className={clsx(
                                            'transition-all duration-300 rounded-full',
                                            index === currentStep
                                                ? 'w-6 h-2 bg-gradient-to-r from-indigo-500 to-purple-500'
                                                : index < currentStep
                                                    ? 'w-2 h-2 bg-purple-300 hover:bg-purple-400'
                                                    : 'w-2 h-2 bg-gray-200 hover:bg-gray-300'
                                        )}
                                        aria-label={`Go to step ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Card Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={handleSkip}
                                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors font-medium"
                                >
                                    Skip Tour
                                </button>

                                <div className="flex items-center gap-2">
                                    {!isFirstStep && (
                                        <button
                                            onClick={handlePrev}
                                            disabled={isTransitioning}
                                            className={clsx(
                                                'flex items-center gap-1.5 px-4 py-2.5 rounded-xl',
                                                'text-sm font-semibold',
                                                'text-gray-700 hover:text-gray-900',
                                                'bg-gray-100 hover:bg-gray-200',
                                                'transition-all duration-200',
                                                'disabled:opacity-50 disabled:cursor-not-allowed'
                                            )}
                                        >
                                            <ChevronLeftIcon className="w-4 h-4" />
                                            Back
                                        </button>
                                    )}

                                    <button
                                        onClick={handleNext}
                                        disabled={isTransitioning}
                                        className={clsx(
                                            'flex items-center gap-1.5 px-5 py-2.5 rounded-xl',
                                            'text-sm font-semibold text-white',
                                            'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500',
                                            'hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600',
                                            'shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40',
                                            'transition-all duration-200',
                                            'disabled:opacity-50 disabled:cursor-not-allowed'
                                        )}
                                    >
                                        {isLastStep ? (
                                            <>
                                                <CheckCircleIcon className="w-4 h-4" />
                                                Finish
                                            </>
                                        ) : (
                                            <>
                                                Next
                                                <ChevronRightIcon className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Keyboard hints */}
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 text-white/60 text-sm z-[1001]">
                        <span className="flex items-center gap-2">
                            <kbd className="px-2 py-1 rounded bg-white/10 text-xs font-mono">←</kbd>
                            <kbd className="px-2 py-1 rounded bg-white/10 text-xs font-mono">→</kbd>
                            <span>Navigate</span>
                        </span>
                        <span className="flex items-center gap-2">
                            <kbd className="px-2 py-1 rounded bg-white/10 text-xs font-mono">ESC</kbd>
                            <span>Close</span>
                        </span>
                    </div>
                </div>
            )}
        </>
    );
};

export default TourGuide;
